// Main application shell - provides navigation and layout for authenticated users
import React, { useState, useCallback } from "react";
import { Menu, LogOut, Users, Trash2, User } from 'lucide-react';
import { useRestAuth } from "../../providers/RestAuthProvider";
import { useLocalization } from "../../providers/LocalizationProvider";
import ThemesSection from "../../features/ThemesSection";
import UsersSection from "../../features/UsersSection";
import QuestionsSection from "../../features/QuestionsSection";
import ResponsesSection from "../../features/AnswersSection";
import ProfileSection from "../../features/ProfileSection";
import LanguageSelector from "../ui/LanguageSelector";
import { Button } from "../ui/Button";
import { Checkbox } from "../ui/Checkbox";
import { logger } from "../../lib/utils/logger";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/DropdownMenu";
import logoLong from "../../assets/constellation-logo-long.png";
import { USER_ROLES } from "../../lib/config/constants";
import { getUserDisplayName } from "../../lib/models/User";

export default function AdminShell({ data }) {
  // Local state for navigation and UI
  const [tab, setTab] = useState("themes");
  const [showTrash, setShowTrash] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  
  // Get hooks for authentication and localization
  const { user, logout } = useRestAuth();
  const { t } = useLocalization();

  // Centralized data loading - ensure all entities are loaded on mount
  React.useEffect(() => {
    const loadAllData = async () => {
      if (data && data.reload && isInitialLoading) {
        logger.info("AdminShell: Loading all entities for centralized data management");
        try {
          setIsInitialLoading(true);
          setLoadingError(null);
          // Load all entities (themes, users, questions, responses)
          await data.reload();
          logger.info("AdminShell: All entities loaded successfully");
        } catch (error) {
          logger.error("AdminShell: Failed to load entities", error);
          setLoadingError(error.message || "Failed to load data");
        } finally {
          setIsInitialLoading(false);
        }
      }
    };

    loadAllData();
  }, [data, isInitialLoading]);

  // Retry function for failed loading
  const retryLoading = useCallback(() => {
    setLoadingError(null);
    setIsInitialLoading(true);
  }, []);


  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="w-full flex justify-left">
            <img src={logoLong} alt="Constellation Sociale" height={80} style={{ height: 80, objectFit: 'contain' }} />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2 text-sm bg-card px-3 py-2 rounded-lg border hover:bg-accent transition-colors cursor-pointer" title={t('navigation.showTrash')}>
              <Checkbox 
                id="show-trash"
                checked={showTrash} 
                onCheckedChange={setShowTrash}
              />
              <label htmlFor="show-trash" className="flex items-center gap-2 cursor-pointer">
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </label>
            </div>
            <LanguageSelector />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="default" size="icon">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">User menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setTab('profile')}>
                  <User className="h-4 w-4 mr-2" />
                  {t('navigation.profile')}
                </DropdownMenuItem>
                {user?.role === USER_ROLES.ADMIN && (
                  <>
                    <DropdownMenuItem onClick={() => setTab('users')}>
                      <Users className="h-4 w-4 mr-2" />
                      {t('navigation.users')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem 
                  onClick={logout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <nav className="bg-card rounded-lg border shadow-sm p-1 flex gap-1 overflow-x-auto justify-between items-center">
          <div className="flex gap-1">
            {[
              {key:'themes',labelKey:'navigation.themes'},
              {key:'questions',labelKey:'navigation.questions'},
              {key:'responses',labelKey:'navigation.responses'},
            ].map(it => (
              <Button 
                key={it.key} 
                onClick={()=>setTab(it.key)}
                variant={tab===it.key ? "default" : "ghost"}
                size="sm"
                className="whitespace-nowrap"
              > 
                {t(it.labelKey)}
              </Button>
            ))}
          </div>
          {user && (
            <div className="flex items-center gap-2 text-sm bg-background px-3 py-1 rounded border ml-2">
              <span className="text-muted-foreground">{t('auth.connectedAs')}</span>
              <span className="font-medium">{getUserDisplayName(user)}</span>
              <span className="text-muted-foreground capitalize">({t(`roles.${user.role}`)})</span>
            </div>
          )}
        </nav>

        <main className="grid gap-4" key={refreshKey}>
          {isInitialLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">{t('common.loading')}</p>
              </div>
            </div>
          ) : loadingError ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="text-red-500 mb-4">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('common.loadingError')}</h3>
                <p className="text-muted-foreground mb-4">{loadingError}</p>
                <Button onClick={retryLoading} variant="outline">
                  {t('common.retry')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {tab==='themes' && <ThemesSection showTrash={showTrash} dataSource={data} />}
              {tab==='users' && <UsersSection showTrash={showTrash} dataSource={data} />}
              {tab==='questions' && <QuestionsSection showTrash={showTrash} dataSource={data} />}
              {tab==='responses' && <ResponsesSection showTrash={showTrash} dataSource={data} />}
              {tab==='profile' && <ProfileSection dataSource={data} />}
            </>
          )}
        </main>

        <footer className="text-center text-xs text-muted-foreground py-6">
          <a 
            href="https://constellation-sociale.org/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 transition-colors"
          >
            {t('app.organizationName')}
          </a>
          {' '}{t('app.copyright')}
        </footer>
      </div>
    </div>
  );
}